/**
 * Request all the sample points for the current enterprise.
 */

import { message as antdMessage } from 'antd';
import keyBy from 'lodash/keyBy';
import { REHYDRATE } from 'redux-persist';
import { SagaIterator } from 'redux-saga';
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';

import { notificationClient } from 'clients';
import { selectDefaultSite } from 'store/modules/localUserSettings/selectors';
import NetworkActionTypes from 'store/modules/network/constants';
import { selectNetworkOnline } from 'store/modules/network/selectors';
import {
  selectNotificationsPendingAcknowledgements,
  selectNotificationsState
} from 'store/modules/notifications/selectors';
import { selectSamplePointsState } from 'store/modules/samplePoints/selectors';
import { SamplePointsState } from 'store/modules/samplePoints/types';
import trackEvent from 'store/modules/tracking/actions';
import { EventType, TrackingEvent } from 'store/modules/tracking/types';
import { AcknowledgeNotificationsResponseDto } from 'types/dto/notification.dto';
import { AssetTypeCode } from 'types/models/asset-type';
import Notification, {
  IPCameraEvent,
  NotificationEventType,
  NotificationId,
  SamplePointEvent
} from 'types/models/notification';
import { APIResponseParsed, PaginatedResponse } from 'types/response';
import { getNotificationEventType } from 'utils/Notification/notification-event-type';
import { sanitiseNotifications } from 'utils/Notification/sanitise-notifications';

import {
  acknowledgeNotificationFailure,
  acknowledgeNotificationSuccess,
  bulkAcknowledgeNotifications as bulkAcknowledgeNotificationsAction,
  bulkAcknowledgeNotificationsFailure,
  bulkAcknowledgeNotificationsSuccess,
  loadPaginatedNotifications,
  loadPaginatedNotificationsFailure,
  loadPaginatedNotificationsSuccess,
  loadRecentUnacknowledgedNotifications,
  loadRecentUnacknowledgedNotificationsFailure,
  loadRecentUnacknowledgedNotificationsSuccess,
  removePendingAcknowledgement,
  setNotification,
  setPaginatedNotifications,
  setRecentUnacknowledgedNotifications
} from './actions';
import ActionTypes from './constants';

// ==============================
// PURE FUNCTIONS
// ==============================
const getTrackingDataWhenAcknowledgingNotification = (
  pendingNotification: Notification,
  samplePointsState: SamplePointsState,
  isSuccess: boolean
): TrackingEvent | undefined => {
  const event = pendingNotification.event;
  const eventType = getNotificationEventType(event);
  switch (eventType) {
    case NotificationEventType.SAMPLE_POINT_EVENT: {
      const samplePointEvent = event as SamplePointEvent;
      const assetTypeId: AssetTypeCode = samplePointsState[samplePointEvent.samplePoint.id]?.assetTypeId
        || AssetTypeCode.BATTERY; // Battery sample point is not represented in the store
      return {
        type: EventType.NOTIFICATION_ACKNOWLEDGED,
        data: {
          status: isSuccess,
          samplePointId: samplePointEvent.samplePoint.id,
          assetTypeId,
          eventLevel: samplePointEvent.level,
          eventMessage: samplePointEvent.message
        }
      };
    }
    case NotificationEventType.IP_CAMERA_EVENT: {
      const ipCameraEvent = event as IPCameraEvent;
      return {
        type: EventType.THIRD_PARTY_NOTIFICATION_ACKNOWLEDGED,
        data: {
          status: isSuccess,
          // @ts-ignore TODO fix 'ipCameraId' does not exist on type TrackingEvent
          ipCameraId: ipCameraEvent.ipCamera.id,
          assetTypeId: AssetTypeCode.SECURITY_CAMERA,
          eventLevel: ipCameraEvent.level,
          eventMessage: ipCameraEvent.message
        }
      };
    }
    default:
      return undefined;
  }
};

// ==============================
// SAGAS
// ==============================
function* requestPaginatedNotifications(
  action: ReturnType<typeof loadPaginatedNotifications>
): SagaIterator {
  const {
    enterpriseId,
    siteId,
    currentPage = 1
  } = action.payload;

  const response: APIResponseParsed<PaginatedResponse<Notification>> = yield call(
    notificationClient.fetchPaginatedNotifications,
    enterpriseId,
    siteId,
    currentPage
  );
  if (response.data) {
    const { data, count } = response.data;
    const notifications: Notification[] = sanitiseNotifications(data);

    yield all([
      put(setPaginatedNotifications(enterpriseId, keyBy(notifications, 'id'), currentPage, count)),
      put(loadPaginatedNotificationsSuccess())
    ]);
  } else {
    const errorMessage = response.error.message || 'Sorry, something went wrong.';
    yield put(loadPaginatedNotificationsFailure(errorMessage));
  }
}

function* requestRecentUnacknowledgedNotifications(
  action: ReturnType<typeof loadRecentUnacknowledgedNotifications>
): SagaIterator {
  const { enterpriseId } = action.payload;

  const response: APIResponseParsed<Notification[]> = yield call(
    notificationClient.fetch7DayUnacknowledgedNotifications,
    enterpriseId
  );
  if (response.data) {
    const notifications: Notification[] = sanitiseNotifications(response.data);

    yield all([
      put(setRecentUnacknowledgedNotifications(enterpriseId, keyBy(notifications, 'id'))),
      put(loadRecentUnacknowledgedNotificationsSuccess())
    ]);
  } else {
    const errorMessage = response.error.message || 'Sorry, something went wrong.';
    yield put(loadRecentUnacknowledgedNotificationsFailure(errorMessage));
  }
}

function* acknowledgeNotifications() {
  const networkOnline: ReturnType<typeof selectNetworkOnline> = yield select(selectNetworkOnline);
  if (!networkOnline) return;

  const notificationsState: ReturnType<typeof selectNotificationsState> = yield select(selectNotificationsState);
  const notifications: Record<NotificationId, Notification> = {
    ...notificationsState.recentUnacknowledgedData,
    ...notificationsState.paginatedData
  };

  const samplePointsState: SamplePointsState = yield select(
    selectSamplePointsState
  );

  const pendingAcknowledgements: ReturnType<
    typeof selectNotificationsPendingAcknowledgements
  > = yield select(selectNotificationsPendingAcknowledgements);

  for (const key in pendingAcknowledgements) {
    const notificationId = Number(key);
    const response: APIResponseParsed<Notification> = yield call(
      notificationClient.acknowledgeNotification,
      notificationId
    );

    const acknowledgedNotification = response.data;
    if (acknowledgedNotification) {
      antdMessage.success('Notification acknowledged');

      yield all([
        put(removePendingAcknowledgement(notificationId)),
        put(setNotification(acknowledgedNotification)),
        put(acknowledgeNotificationSuccess())
      ]);
      const trackingData = getTrackingDataWhenAcknowledgingNotification(
        acknowledgedNotification,
        samplePointsState,
        true
      );
      if (trackingData) {
        yield put(trackEvent(trackingData));
      }
    } else {
      // Suppress error handling in the event of a Network Error.
      if (response.error.isNetworkError) return;

      const errorMessage = response.error.message || 'Sorry, something went wrong.';
      antdMessage.error('Failed to acknowledge the notification. Please try again.');

      yield all([
        // Remove from pending acknowledgements to prevent retry loops. Unlike network errors
        // (which are handled above), non-network errors (e.g., server errors, validation failures)
        // are unlikely to resolve on retry, so we stop attempting after the first failure.
        put(removePendingAcknowledgement(notificationId)),

        put(acknowledgeNotificationFailure(errorMessage))
      ]);
      const trackingData = getTrackingDataWhenAcknowledgingNotification(
        notifications[notificationId],
        samplePointsState,
        false
      );
      if (trackingData) {
        yield put(trackEvent(trackingData));
      }
    }
  }
}

function* bulkAcknowledgeNotifications(
  action: ReturnType<typeof bulkAcknowledgeNotificationsAction>
) {
  const {
    enterpriseId,
    currentPage
  }: ReturnType<typeof selectNotificationsState> = yield select(selectNotificationsState);

  if (!enterpriseId) return;

  const {
    notificationIds,
    onSuccess,
    onFailure
  } = action.payload;

  const response: APIResponseParsed<AcknowledgeNotificationsResponseDto> = yield call(
    notificationClient.acknowledgeNotifications,
    notificationIds
  );

  if (response.data) {
    const { id: siteId }: ReturnType<typeof selectDefaultSite> = yield select(selectDefaultSite);

    yield all([
      put(loadRecentUnacknowledgedNotifications(enterpriseId)),
      put(loadPaginatedNotifications(enterpriseId, siteId, currentPage))
    ]);

    const { acknowledged, failed } = response.data;
    yield put(bulkAcknowledgeNotificationsSuccess());
    onSuccess(acknowledged, failed);
  } else {
    const errorMessage = response.error.message || 'Sorry, something went wrong.';
    yield put(bulkAcknowledgeNotificationsFailure(errorMessage));
    onFailure(errorMessage);
  }
}

// ==============================
// REGISTRATION
// ==============================
function* watchBulkAcknowledgeNotificationsRequest() {
  yield takeLatest(
    ActionTypes.BULK_ACKNOWLEDGE_NOTIFICATIONS_REQUEST,
    bulkAcknowledgeNotifications
  );
}

function* watchLoadPaginatedNotificationsRequest() {
  yield takeLatest(
    ActionTypes.LOAD_PAGINATED_NOTIFICATIONS_REQUEST,
    requestPaginatedNotifications
  );
}

function* watchLoadRecentUnacknowledgedNotificationsRequest() {
  yield takeLatest(
    ActionTypes.LOAD_RECENT_UNACKNOWLEDGED_NOTIFICATIONS_REQUEST,
    requestRecentUnacknowledgedNotifications
  );
}

function* watchSetPendingAcknowledgement() {
  yield takeLatest(
    [
      REHYDRATE,
      NetworkActionTypes.SET_NETWORK_ONLINE,
      ActionTypes.SET_PENDING_ACKNOWLEDGEMENT
    ],
    acknowledgeNotifications
  );
}

// ==============================
// EXPORT
// ==============================
export default function* notificationsSaga() {
  yield all([
    fork(watchBulkAcknowledgeNotificationsRequest),
    fork(watchLoadPaginatedNotificationsRequest),
    fork(watchLoadRecentUnacknowledgedNotificationsRequest),
    fork(watchSetPendingAcknowledgement)
  ]);
}
